import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  keyframes,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/fr";
import { TOKENS } from "./styled";

dayjs.extend(isoWeek);
dayjs.locale("fr");

export type PlanningSlot = {
  date: string;
  halfday: string; // "am" | "pm"
  is_available: boolean;
  fill_percentage: number;
  hour_from: string | null;
  hour_to: string | null;
  has_extra_price?: boolean;
  extra_price?: number;
};

type Props = {
  slots: PlanningSlot[];
  loading: boolean;
  selected: { date: string; halfday: string } | null;
  onSelect: (sel: {
    date: string;
    halfday: string;
    hourFrom: string | null;
    hourTo: string | null;
  }) => void;
};

const DAY_LABELS = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

type DayCell = {
  date: Dayjs;
  morning: PlanningSlot | null;
  afternoon: PlanningSlot | null;
};

type WeekRow = {
  start: Dayjs;
  end: Dayjs;
  days: DayCell[];
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const calendarFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.99); }
  to { opacity: 1; transform: scale(1); }
`;

const isShowable = (slot: PlanningSlot): boolean =>
  slot.is_available && slot.fill_percentage < 100;

const groupByWeek = (slots: PlanningSlot[]): WeekRow[] => {
  const visible = slots.filter(isShowable);
  if (!visible.length) return [];
  const byKey: Record<string, PlanningSlot> = {};
  visible.forEach((s) => {
    byKey[`${s.date}|${s.halfday}`] = s;
  });
  const dates = Array.from(new Set(visible.map((s) => s.date))).sort();
  if (!dates.length) return [];
  const first = dayjs(dates[0]).startOf("isoWeek");
  const last = dayjs(dates[dates.length - 1]).endOf("isoWeek");
  const weeks: WeekRow[] = [];
  let cursor = first;
  while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
    const weekStart = cursor;
    const days: DayCell[] = [];
    for (let i = 0; i < 7; i += 1) {
      const d = weekStart.add(i, "day");
      const key = d.format("YYYY-MM-DD");
      days.push({
        date: d,
        morning: byKey[`${key}|am`] || null,
        afternoon: byKey[`${key}|pm`] || null,
      });
    }
    weeks.push({ start: weekStart, end: weekStart.add(6, "day"), days });
    cursor = cursor.add(7, "day");
  }
  return weeks;
};

// ─── Slot pill ─────────────────────────────────────────────────────────────────
const SlotPill: React.FC<{
  slot: PlanningSlot;
  halfday: "am" | "pm";
  selected: boolean;
  onClick?: () => void;
  slotCount: number;
}> = ({ slot, halfday, selected, onClick, slotCount }) => {
  const isAM = halfday === "am";
  const restingGradient = isAM
    ? "linear-gradient(135deg, #fffdf5 0%, #fef3c7 100%)"
    : "linear-gradient(135deg, #ebf4ff 0%, #c3dafe 100%)";
  const hoverGradient = isAM
    ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
    : `linear-gradient(135deg, ${TOKENS.ACCENT_BLUE} 0%, #2b6cb0 100%)`;
  const restColor = isAM ? "#92400e" : "#2b6cb0";
  const borderColor = isAM ? "#f59e0b" : TOKENS.ACCENT_BLUE;
  const restingShadow = isAM
    ? "0 3px 12px rgba(245, 158, 11, 0.24)"
    : "0 3px 12px rgba(49, 130, 206, 0.24)";
  const hoverShadow = isAM
    ? "0 8px 24px rgba(245, 158, 11, 0.4)"
    : "0 8px 24px rgba(49, 130, 206, 0.4)";
  const selectedStyles = selected
    ? {
        background: TOKENS.ACCENT_BLUE,
        color: TOKENS.WHITE,
        border: `1.5px solid ${TOKENS.ACCENT_BLUE}`,
        boxShadow: "0 4px 14px rgba(49, 130, 206, 0.35)",
        transform: "scale(1.03)",
        outline: `3px solid ${TOKENS.GRAY_800}`,
        outlineOffset: 2,
      }
    : {};

  const pillFontSize =
    slotCount >= 3
      ? { xs: "0.62rem", sm: "0.7rem", md: "0.78rem" }
      : { xs: "0.68rem", sm: "0.78rem", md: "0.85rem" };
  const pillPadding =
    slotCount >= 3
      ? { xs: "5px 6px", sm: "6px 8px", md: "8px 10px" }
      : { xs: "7px 8px", sm: "9px 12px", md: "10px 14px" };
  const pillRadius = { xs: "8px", sm: "9px", md: "10px" };

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        padding: pillPadding,
        borderRadius: pillRadius,
        fontSize: pillFontSize,
        fontWeight: 600,
        cursor: "pointer",
        userSelect: "none",
        overflow: "hidden",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        textAlign: "center",
        letterSpacing: "0.01em",
        background: restingGradient,
        color: restColor,
        border: `2px solid ${borderColor}`,
        boxShadow: restingShadow,
        "&:hover": {
          background: hoverGradient,
          color: TOKENS.WHITE,
          borderColor,
          boxShadow: hoverShadow,
        },
        "&:active": {
          transform: "translateY(0) scale(0.98)",
        },
        ...selectedStyles,
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          sx={{
            fontSize: slotCount >= 3 ? "0.42rem" : { xs: "0.5rem", sm: "0.6rem" },
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            opacity: 0.75,
            mb: "2px",
            color: "inherit",
          }}
        >
          {isAM ? "Matin" : "Après-midi"}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.82rem" },
            fontWeight: 700,
            color: "inherit",
            lineHeight: 1.1,
          }}
        >
          {slot.hour_from && slot.hour_to
            ? `${slot.hour_from.slice(0, 5)} - ${slot.hour_to.slice(0, 5)}`
            : "—"}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Calendar ─────────────────────────────────────────────────────────────────
const PlanningCalendar: React.FC<Props> = ({
  slots,
  loading,
  selected,
  onSelect,
}) => {
  const weeks = React.useMemo(() => groupByWeek(slots), [slots]);
  const todayKey = dayjs().format("YYYY-MM-DD");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!slots.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Aucun créneau disponible pour ces prestations et cette adresse.
      </Alert>
    );
  }

  return (
    <Box sx={{ animation: `${calendarFadeIn} 0.4s ease-out` }}>
      {weeks.map((week, idx) => (
        <Box key={idx} mb={3}>
          {/* Week header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 1.5,
              borderBottom: `2px solid ${TOKENS.GRAY_200}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: TOKENS.PRIMARY_BLUE,
                letterSpacing: "-0.01em",
              }}
            >
              Semaine du{" "}
              <Box
                component="span"
                sx={{ color: TOKENS.ACCENT_BLUE, fontWeight: 700 }}
              >
                {week.start.format("D")} — {week.end.format("D MMM YYYY")}
              </Box>
            </Typography>
          </Box>

          {/* Grid — 2 col on phones, 4 col tablet, 7 col desktop */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
                md: "repeat(7, 1fr)",
              },
              gap: { xs: "6px", sm: "8px", md: "10px" },
            }}
          >
            {week.days.map((day, i) => {
              const hasSlots = Boolean(day.morning || day.afternoon);
              const key = day.date.format("YYYY-MM-DD");
              const isToday = key === todayKey;
              const slotCount = (day.morning ? 1 : 0) + (day.afternoon ? 1 : 0);

              return (
                <Box
                  key={key}
                  sx={{
                    background: hasSlots ? TOKENS.WHITE : TOKENS.GRAY_100,
                    borderRadius: { xs: "10px", sm: "12px", md: "14px" },
                    border: `1.5px solid ${
                      isToday ? TOKENS.ACCENT_BLUE : TOKENS.GRAY_400
                    }`,
                    borderWidth: isToday ? 2 : 1.5,
                    padding: {
                      xs: "10px 10px",
                      sm: "12px 10px",
                      md: "14px 12px",
                    },
                    minHeight: { xs: 116, sm: 140, md: 160 },
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `${fadeIn} 0.3s ease-out both`,
                    animationDelay: `${i * 0.04}s`,
                    position: "relative",
                    overflow: "hidden",
                    opacity: hasSlots ? 1 : 0.55,
                    boxShadow: isToday
                      ? "0 0 0 3px rgba(49,130,206,0.1)"
                      : "none",
                    "&:hover": hasSlots
                      ? {
                          borderColor: TOKENS.ACCENT_BLUE,
                          boxShadow: "0 8px 24px rgba(49,130,206,0.12)",
                          transform: { md: "translateY(-2px)" },
                        }
                      : {},
                  }}
                >
                  {/* Day header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${TOKENS.GRAY_300}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: isToday
                          ? TOKENS.ACCENT_BLUE
                          : TOKENS.LIGHT_BLUE,
                      }}
                    >
                      {DAY_LABELS[day.date.isoWeekday() - 1]}
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        transition: "all 0.2s",
                        background: isToday
                          ? TOKENS.ACCENT_BLUE
                          : "transparent",
                        color: isToday ? TOKENS.WHITE : TOKENS.PRIMARY_BLUE,
                        boxShadow: isToday
                          ? "0 2px 8px rgba(49,130,206,0.35)"
                          : "none",
                      }}
                    >
                      {day.date.format("D")}
                    </Box>
                  </Box>

                  {/* Slots */}
                  {hasSlots ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: "4px", sm: "6px", md: "8px" },
                        flex: 1,
                      }}
                    >
                      {day.morning && (
                        <SlotPill
                          slot={day.morning}
                          halfday="am"
                          selected={
                            selected?.date === key && selected.halfday === "am"
                          }
                          slotCount={slotCount}
                          onClick={() =>
                            onSelect({
                              date: key,
                              halfday: "am",
                              hourFrom: day.morning!.hour_from,
                              hourTo: day.morning!.hour_to,
                            })
                          }
                        />
                      )}
                      {day.afternoon && (
                        <SlotPill
                          slot={day.afternoon}
                          halfday="pm"
                          selected={
                            selected?.date === key && selected.halfday === "pm"
                          }
                          slotCount={slotCount}
                          onClick={() =>
                            onSelect({
                              date: key,
                              halfday: "pm",
                              hourFrom: day.afternoon!.hour_from,
                              hourTo: day.afternoon!.hour_to,
                            })
                          }
                        />
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: TOKENS.GRAY_500,
                          fontStyle: "italic",
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          textAlign: "center",
                        }}
                      >
                        Complet
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default PlanningCalendar;
